import { AppSumoLicense, Bookmark, CheckIn, GetAccountResponse, Location, ObjectId, SubscriptionFeature } from '../../types';
import {
    aIUsesThisMonth,
    addBookmark,
    addProfileFollow,
    deleteAccount,
    getAccount,
    logout,
    redirectToLogin,
    removeBookmark,
    removeProfileFollow,
    routeUsesThisMonth,
} from '../api/account-api';
import { createCheckin, getCheckinKey, getCheckinMapFromCheckins, getCheckins, removeCheckin } from '../api/checkin-api';
import { getAccountInformation, logWithAmplitude, setGoogleUserId } from '../api/api';
import { getFromLocalStorage, isDefined, isValidString } from '../utils';

import { State } from '../state/state-management/state';
import { getMainPage } from '../page-name';
import organizationInfo from './organization-info';
import { property } from '../state/state-management/decorators/property';
import subscriptionInfo from './subscription-info';

interface AccountMetadata {
    secret: string;
    device_name: string;
    expires_at: string;
}

class AccountInfo extends State {
    @property({ type: Boolean }) isReady: boolean = false;

    @property({ type: Boolean }) isAuthenticated: boolean = false;

    @property({ type: String }) fullName: string = '';

    @property({ value: [], type: Array }) bookmarks: Bookmark[] = [];

    @property({ value: [], type: Array }) followedProfiles: ObjectId[] = [];

    @property({ type: String }) phoneNumber?: string;

    @property({ type: String }) email: string = '';

    @property({ type: String }) image: string = '';

    @property({ type: Boolean }) isAppSumoUser?: boolean | null;

    @property({ type: Boolean }) legacy_account?: boolean | null;

    @property({ type: Number }) ai_tool_uses_this_month?: number | null;

    @property({ type: Boolean }) routes_uses_this_month?: number | null;

    @property({ type: Object }) appSumoLicenseDetails?: AppSumoLicense | null;

    @property({ type: Boolean }) migrated_from_personal?: boolean | null;

    @property({ type: Object }) account?: GetAccountResponse | null;

    @property({ type: Map }) enabledFeatures: SubscriptionFeature[] = [];

    @property({ type: Map }) account_checkins: Map<String, CheckIn[]> = new Map<
        String,
        CheckIn[]
    >();

    id: ObjectId = { $oid: '' };

    accountMetadata?: AccountMetadata;

    static amplitude_callsite: string = 'Account Info Cache';

    // call this in sign in page, and then in main-page. in main-page, check if data isReady, if not, instantiate
    async instantiateAccountData(reload: boolean = true) {
        if (reload) {
            this.isReady = false;
        }

        const accountInfo = await getAccount(AccountInfo.amplitude_callsite);
        const accountMetadata = getAccountInformation();

        if (isDefined(accountInfo) && isDefined(accountMetadata)) {
            this.isAuthenticated = true;
            this.account = accountInfo;
            this.fullName = accountInfo.full_name ?? '';
            this.image = accountInfo.image_url ?? '';
            this.bookmarks = accountInfo.bookmarks;
            this.followedProfiles = accountInfo.followed_profiles;
            this.legacy_account = accountInfo.legacy_account;
            this.phoneNumber = accountInfo.phone_number;
            this.email = accountInfo.email;
            this.enabledFeatures = accountInfo.enabled_features;
            this.migrated_from_personal = accountInfo.migrated_from_personal;

            this.accountMetadata = {
                secret: accountMetadata.secret,
                device_name: accountMetadata.device_name,
                expires_at: accountMetadata.expires_at,
            };
            this.id = accountInfo._id;
            this.ai_tool_uses_this_month = aIUsesThisMonth(accountInfo.ai_tool_uses);
            this.routes_uses_this_month = routeUsesThisMonth(accountInfo.routes_uses);

            const checkins = await getCheckins(AccountInfo.amplitude_callsite);
            this.account_checkins = getCheckinMapFromCheckins(checkins);

            this.appSumoLicenseDetails = accountInfo.appsumo_license;

            if (((this.account.chargebee_subscription?.plan_id?.includes('AppSumo')) === true) && this.appSumoLicenseDetails?.appsumo_plan_id !== 'proxi_free') {
                this.isAppSumoUser = true;
            }

            // instantiate organization data if user is authenticated
            const organization_id = getFromLocalStorage('organization_id') ?? organizationInfo.id?.$oid;
            if (isValidString(organization_id) && organization_id !== 'null' && organization_id !== 'undefined') {
                await organizationInfo.instantiateOrganizationDataAsAuthenticatedUser(organization_id);
            } else {
                logWithAmplitude(AccountInfo.amplitude_callsite, 'Account Subscription Status', {
                subscription_id: accountInfo?.chargebee_subscription?.subscription_id,
                chargebee_customer_id: accountInfo?.chargebee_subscription?.customer_id,
                chargebee_subscription_plan: accountInfo?.chargebee_subscription?.plan_id,
                login_type: 'personal'
            });
            }

            // needs to stay after organization is instantiated
            subscriptionInfo.instantiateSubscriptionInfo();
        } else {
            this.isAuthenticated = false;
        }

        this.isReady = true;
        void setGoogleUserId(this.email, this.account?._id.$oid);
    }


    async logout(shouldRedirectToLogin: boolean = true) {
        await logout('Account Info');

        this.clearAccountData();

        if (shouldRedirectToLogin) {
            redirectToLogin();
        }

        await getMainPage().reload();
    }

    async deleteAccountInfo() {
        await deleteAccount('Account info');
        this.clearAccountData();

        redirectToLogin();
        await getMainPage().reload();
    }

    clearAccountData() {
        this.isAuthenticated = false;
        this.fullName = '';
        this.bookmarks = [];
        this.followedProfiles = [];
        this.email = '';
        this.accountMetadata = undefined;
        this.account_checkins = new Map<String, CheckIn[]>();
        organizationInfo.clearOrganizationData();
    }

    async unbookmarkTopic(topicId: string, topicKey?: string) {
        this.bookmarks = this.bookmarks.filter((b) => b.topic_id.$oid !== topicId);
        await removeBookmark('Account Info', topicId, topicKey);
    }

    async bookmarkTopic(topicId: string, topicKey?: string) {
        this.bookmarks.push({ topic_id: { $oid: topicId }, topic_key: topicKey });
        await addBookmark('Account Info', topicId, topicKey);
    }

    async followProfile(profileId: string) {
        this.followedProfiles.push({ $oid: profileId });
        await addProfileFollow('Account info', profileId);
    }

    async unfollowProfile(profileId: string) {
        this.followedProfiles = this.followedProfiles.filter((p) => p.$oid !== profileId);
        await removeProfileFollow('Account info', profileId);
    }

    async createStandardCheckin(topic_id: ObjectId, location: Location) {
        const createCheckinRequest = {
            topic_id,
            location,
        };

        const response = await createCheckin('Account info', createCheckinRequest);

        const new_checkins = new Map<String, CheckIn[]>();

        // add the new checkin to the account_checkins map
        if (this.account_checkins.has(getCheckinKey(location))) {
            const existingCheckins = this.account_checkins.get(getCheckinKey(location)) ?? [];
            existingCheckins.push({
                _id: response._id,
                timestamp: response.timestamp,
                topic_id: response.topic_id,
                location: response.location,
            });
            new_checkins.set(getCheckinKey(location), existingCheckins);
        }
        else {
            new_checkins.set(getCheckinKey(location), [{
                _id: response._id,
                timestamp: response.timestamp,
                topic_id: response.topic_id,
                location: response.location,
            }]);
        }
        // update the account_checkins map with the new checkin
        this.account_checkins.set(getCheckinKey(location), new_checkins.get(getCheckinKey(location)) ?? []);
    }

    async removeCheckin(location: Location) {
        if (!this.isAuthenticated) {
            return;
        }
        const rmeoved_key =
            getCheckinKey(location);
;
        const checkins = await getCheckins(AccountInfo.amplitude_callsite);

        const checkin_id = checkins.find(
            (checkin) =>
                checkin.location.place_id === location.place_id &&
                checkin.location.latLng.lat === location.latLng.lat &&
                checkin.location.latLng.lng === location.latLng.lng
        )?._id.$oid;


        if (isDefined(checkin_id)) {
            await removeCheckin('Account info', checkin_id);

            const new_checkins = new Map<String, CheckIn[]>();

            this.account_checkins.delete(rmeoved_key);

            this.account_checkins = new_checkins;
        }
    }

    async getAccountCheckins() {
        if (this.isAuthenticated) {
            const checkins = await getCheckins(AccountInfo.amplitude_callsite);
            return getCheckinMapFromCheckins(checkins);
        } else {
            return new Map<String, CheckIn[]>();
        }
    }

    async refreshCheckins() {
        if (this.isAuthenticated) {
            const checkins = await getCheckins(AccountInfo.amplitude_callsite);
            this.account_checkins = getCheckinMapFromCheckins(checkins);
        }
    }
}

const accountInfo = new AccountInfo();

export default accountInfo;
